/**
 * Easy Pets — sticky add-to-cart bar.
 *
 * Watches the page's main Add-to-cart button via IntersectionObserver and
 * reveals the fixed bar once that button scrolls out of view. The bar's button
 * forwards to the real product form button, reusing the theme's existing
 * variant validation, AJAX add, and cart-notification flow.
 */
class EasypetsStickyAtc extends HTMLElement {
  #observer = null;
  #watchTarget = null;

  connectedCallback() {
    const selector = this.dataset.watchSelector || '.product-form__submit';
    this.#watchTarget = document.querySelector(selector);

    // No main buy button on the page — leave the bar hidden and do nothing.
    if (!this.#watchTarget) return;

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0,
    });
    this.#observer.observe(this.#watchTarget);

    const button = this.querySelector('[data-sticky-add]');
    button?.addEventListener('click', this.#onAddClick);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    const button = this.querySelector('[data-sticky-add]');
    button?.removeEventListener('click', this.#onAddClick);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      // Show the bar only when the real button is above the viewport
      // (scrolled past), not when it's still below the fold on load.
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', scrolledPast);
      this.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
    }
  };

  #onAddClick = (event) => {
    event.preventDefault();
    if (!this.#watchTarget) return;

    // Forward to the real product form: prefer requestSubmit so the theme's
    // <product-form> submit handler runs; fall back to clicking the button.
    const form = this.#watchTarget.closest('form');
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(this.#watchTarget);
    } else {
      this.#watchTarget.click();
    }
  };
}

if (!customElements.get('easypets-sticky-atc')) {
  customElements.define('easypets-sticky-atc', EasypetsStickyAtc);
}
